/**
 * 🏠 Landing Page — Workflow Manager Enterprise
 * Sistema de Execução Operacional — public marketing page.
 * Design: Enterprise dark SaaS (Stripe / Linear / Vercel tier)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, ArrowRight, BarChart3, CheckCircle2, ChevronRight,
  Clock, GitBranch, Map, Shield, Target, TrendingDown,
  Trophy, X, Zap,
} from 'lucide-react';

/* ─────────────────────────── DESIGN TOKENS ─────────────────────────── */

// Color System
const C = {
  bgMain:     '#0B1220',
  bgAlt:      '#0F1628',
  bgCard:     '#121A2B',
  border:     'rgba(255,255,255,0.06)',
  borderHov:  'rgba(255,255,255,0.14)',
  textPri:    '#E6EDF5',
  textSec:    '#9FB0C3',
  textMuted:  '#6B7C93',
  accentA:    '#3B82F6',
  accentB:    '#06B6D4',
  accentC:    '#10B981',
  gradHero:   'linear-gradient(90deg,#60A5FA 0%,#06B6D4 50%,#34D399 100%)',
  gradBtn:    'linear-gradient(135deg,#3B82F6,#2563EB)',
  gradBtnHov: 'linear-gradient(135deg,#60A5FA,#3B82F6)',
} as const;

const cardBase: React.CSSProperties = {
  background: C.bgCard,
  border: `1px solid ${C.border}`,
  borderRadius: '16px',
};

/* ─────────────────────────── DATA ─────────────────────────── */

const PLANS = [
  {
    slug: 'free', name: 'Free', price: 0, priceAnnual: 0,
    description: 'Para experimentar a plataforma',
    features: ['Até 3 usuários', '5 processos', '2 projetos', 'Mapa visual de processos', 'Suporte community'],
    cta: 'Começar em 5 minutos', highlighted: false,
  },
  {
    slug: 'starter', name: 'Equipe', price: 497, priceAnnual: 413,
    description: 'Para times estruturando a execução',
    features: ['Até 10 usuários', '25 processos', '10 projetos', 'Monitor operacional', 'Plano de ação estruturado', 'Suporte por email'],
    cta: 'Começar em 5 minutos', highlighted: false,
  },
  {
    slug: 'pro', name: 'Operação', price: 1490, priceAnnual: 1242,
    description: 'Para empresas que precisam de controle operacional real',
    features: ['Até 50 usuários', '100 processos', '50 projetos', 'Modelos e governança', 'Gamificação', 'API e Webhooks', 'SLA dedicado', 'Suporte prioritário'],
    cta: 'Começar em 5 minutos', highlighted: true,
  },
  {
    slug: 'enterprise', name: 'Enterprise', price: null, priceAnnual: null,
    description: 'Para grandes organizações com operações complexas',
    features: ['Usuários ilimitados', 'Processos ilimitados', 'SSO e LDAP', 'SLA dedicado', 'Onboarding personalizado', 'Consultoria incluída', 'Suporte 24/7'],
    cta: 'Falar com especialista', highlighted: false,
  },
];

const FEATURES = [
  { icon: Map,       title: 'Processo Visual',           desc: 'Visualize o fluxo completo dos seus processos e identifique exatamente onde está travando.',                  accent: '#06B6D4', bg: 'rgba(6,182,212,0.09)' },
  { icon: GitBranch, title: 'Execução de Processos',    desc: 'Transforme seus processos em etapas executáveis com responsáveis e prazos claros.',                            accent: '#3B82F6', bg: 'rgba(59,130,246,0.09)' },
  { icon: Target,    title: 'Plano de Ação',             desc: 'Transforme problemas em ações estruturadas com responsáveis, prazos e acompanhamento de resultados.',          accent: '#10B981', bg: 'rgba(16,185,129,0.09)' },
  { icon: BarChart3, title: 'Monitoramento em Tempo Real', desc: 'Veja exatamente o que está acontecendo agora na sua operação — gargalos, filas e prazos.',                  accent: '#8B5CF6', bg: 'rgba(139,92,246,0.09)' },
  { icon: Shield,    title: 'Governança Operacional',   desc: 'Padronize processos com modelos corporativos versionados e aprovação controlada.',                             accent: '#F59E0B', bg: 'rgba(245,158,11,0.09)' },
  { icon: Trophy,    title: 'Engajamento de Equipe',     desc: 'Engaje suas equipes com rankings, conquistas e reconhecimento por resultados operacionais.',                  accent: '#EC4899', bg: 'rgba(236,72,153,0.09)' },
];

const HOW_IT_WORKS = [
  { step: '01', label: 'Planejar',   icon: Map,          desc: 'Defina seu processo de forma simples e visual.',                                  color: '#06B6D4' },
  { step: '02', label: 'Executar',   icon: GitBranch,    desc: 'As tarefas chegam automaticamente para as pessoas certas.',                       color: '#3B82F6' },
  { step: '03', label: 'Monitorar',  icon: Activity,     desc: 'Acompanhe prazos, filas e gargalos em tempo real.',                               color: '#8B5CF6' },
  { step: '04', label: 'Melhorar',   icon: TrendingDown, desc: 'Ajuste continuamente com base em dados reais.',                                   color: '#10B981' },
];

const SCALE_STATS = [
  { value: '500+',    label: 'processos executados',        icon: Map,       accent: '#06B6D4' },
  { value: '10.000+', label: 'etapas concluídas',           icon: GitBranch, accent: '#3B82F6' },
  { value: '98%',     label: 'de prazos cumpridos',         icon: Clock,     accent: '#10B981' },
  { value: '35%',     label: 'menos retrabalho em média',   icon: Zap,       accent: '#8B5CF6' },
];

const COMPARISON = [
  { traditional: 'Organizam tarefas',             wfm: 'Executa processos reais' },
  { traditional: 'Mostram o que já aconteceu',     wfm: 'Mostra o que está acontecendo agora' },
  { traditional: 'Dependem de controle manual',    wfm: 'Distribui automaticamente para a equipe' },
  { traditional: 'Perdem o contexto do processo',  wfm: 'Mantém o fluxo completo de ponta a ponta' },
];

const TESTIMONIALS = [
  { text: 'Reduzimos o lead time em 28% nos primeiros 60 dias. Hoje sabemos exatamente o que está parado e por quê.',                                        name: 'Carlos Mendes',     role: 'Head de Operações',                  company: 'Metalúrgica Sigma',      initials: 'CM', color: '#3B82F6' },
  { text: 'Hoje temos visibilidade total da operação e sabemos exatamente onde estão os gargalos. Mudou completamente o controle que temos.',                name: 'Ana Paula Ribeiro', role: 'Gerente de Processos',               company: 'LogisBrasil',            initials: 'AP', color: '#06B6D4' },
  { text: 'A padronização de processos eliminou retrabalho e duplicidade em toda a operação. Temos controle corporativo real agora.',                        name: 'Roberto Fonseca',   role: 'Dir. de Excelência Operacional',     company: 'Grupo Financeiro Verde', initials: 'RF', color: '#10B981' },
];

const CLIENTS = ['Metalúrgica Sigma', 'LogisBrasil', 'Grupo Verde', 'TechOps SA', 'IndústriaFlex', 'Oper8 Group'];

/* ─────────────────────────── COMPONENT ─────────────────────────── */

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');

  useEffect(() => {
    document.title = 'Workflow Manager — Sistema de Execução Operacional';
    const desc = 'Crie, execute e controle processos com sua equipe — em tempo real, sem perder visibilidade nem controle.';
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    meta.content = desc;
  }, []);

  const go = () => navigate('/login');
  const sales = () => { window.location.href = `mailto:contato@workflowbrasil.com?subject=${encodeURIComponent('Interesse no Workflow Manager Enterprise')}`; };
  const getPrice = (plan: typeof PLANS[0]) =>
    plan.price === null ? null : billingCycle === 'annual' ? plan.priceAnnual : plan.price;

  /* ── Hover helpers ── */
  const onCardEnter = (e: React.MouseEvent<HTMLElement>, borderColor?: string) => {
    const el = e.currentTarget as HTMLElement;
    el.style.transform = 'translateY(-4px)';
    el.style.borderColor = borderColor ?? C.borderHov;
    el.style.boxShadow = '0 12px 40px rgba(0,0,0,0.3)';
  };
  const onCardLeave = (e: React.MouseEvent<HTMLElement>) => {
    const el = e.currentTarget as HTMLElement;
    el.style.transform = 'none';
    el.style.borderColor = C.border;
    el.style.boxShadow = 'none';
  };

  return (
    <div className="min-h-screen overflow-y-auto h-screen" style={{ background: C.bgMain, color: C.textPri, fontFamily: 'Inter,system-ui,sans-serif' }}>

      {/* ══════════════════════════════════════════════════════════════
           HEADER
      ══════════════════════════════════════════════════════════════ */}
      <header className="sticky top-0 z-50" style={{ background: 'rgba(11,18,32,0.9)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-7xl mx-auto px-8 h-16 flex items-center justify-between">

          <div className="flex items-center gap-3">
            <img src="/assets/logo/workflow-icon.png" alt="Workflow Manager" className="w-9 h-9 object-contain" loading="lazy" />
            <div>
              <div className="text-sm font-bold tracking-tight leading-tight" style={{ color: C.textPri }}>Workflow Manager</div>
              <div className="text-[10px] font-semibold tracking-widest uppercase leading-tight" style={{ color: C.accentB }}>Sistema de Execução Operacional</div>
            </div>
          </div>

          <nav className="hidden md:flex items-center gap-8">
            {([['Como funciona', '#how-it-works'], ['Plataforma', '#explore'], ['Recursos', '#features'], ['Preços', '#pricing']] as [string, string][]).map(([label, href]) => (
              <a key={href} href={href} className="text-sm transition-colors duration-150" style={{ color: C.textMuted }}
                onMouseEnter={e => (e.currentTarget.style.color = C.textPri)}
                onMouseLeave={e => (e.currentTarget.style.color = C.textMuted)}>{label}</a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button onClick={go} className="hidden sm:block text-sm px-4 py-2 rounded transition-colors" style={{ color: C.textSec }}
              onMouseEnter={e => (e.currentTarget.style.color = C.textPri)}
              onMouseLeave={e => (e.currentTarget.style.color = C.textSec)}>
              Entrar
            </button>
            <button onClick={go}
              className="flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded transition-all duration-150"
              style={{ background: C.gradBtn, color: '#fff', boxShadow: '0 0 24px rgba(59,130,246,0.28)' }}
              onMouseEnter={e => { e.currentTarget.style.background = C.gradBtnHov; e.currentTarget.style.boxShadow = '0 4px 28px rgba(59,130,246,0.45)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = C.gradBtn; e.currentTarget.style.boxShadow = '0 0 24px rgba(59,130,246,0.28)'; e.currentTarget.style.transform = 'none'; }}>
              Acessar plataforma <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </header>

      {/* ══════════════════════════════════════════════════════════════
           HERO
      ══════════════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden flex flex-col items-center justify-center" style={{ background: `linear-gradient(180deg,${C.bgMain} 0%,${C.bgAlt} 60%,${C.bgMain} 100%)`, minHeight: '100vh', paddingTop: '120px', paddingBottom: '80px' }}>
        {/* Grid bg */}
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ backgroundImage: `linear-gradient(${C.border} 1px,transparent 1px),linear-gradient(90deg,${C.border} 1px,transparent 1px)`, backgroundSize: '64px 64px' }} />
        {/* Top glow */}
        <div aria-hidden className="absolute -top-32 left-1/2 -translate-x-1/2 pointer-events-none" style={{ width: '1000px', height: '600px', background: 'radial-gradient(ellipse,rgba(59,130,246,0.12) 0%,transparent 65%)', filter: 'blur(48px)' }} />

        <div className="relative w-full max-w-7xl mx-auto px-8">
          {/* Badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2.5 rounded-sm text-[11px] font-semibold px-4 py-1.5" style={{ border: `1px solid rgba(6,182,212,0.3)`, background: 'rgba(6,182,212,0.07)', color: '#67E8F9' }}>
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: C.accentB }} />
              Sistema de Execução Operacional
            </div>
          </div>

          {/* Headline */}
          <h1 className="text-center font-bold tracking-tight mb-6" style={{ fontSize: 'clamp(2.4rem,5vw,4rem)', lineHeight: '1.08', letterSpacing: '-0.02em' }}>
            <span style={{ color: C.textPri }}>Sua operação,</span><br />
            <span style={{ background: C.gradHero, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              executando de verdade.
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-center mx-auto mb-6" style={{ color: C.textSec, fontSize: '1.125rem', lineHeight: '1.7', maxWidth: '640px' }}>
            Crie, execute e controle processos com sua equipe — em tempo real, sem perder visibilidade nem controle.
          </p>

          {/* Bullets */}
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 mb-10">
            {['Saiba exatamente o que está parado', 'Execute sem depender de planilhas', 'Detecte gargalos automaticamente'].map(b => (
              <div key={b} className="flex items-center gap-2 text-sm" style={{ color: C.textSec }}>
                <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: C.accentC }} />{b}
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            <button onClick={go}
              className="flex items-center gap-2 px-8 py-4 rounded text-sm font-bold transition-all duration-150"
              style={{ background: C.gradBtn, color: '#fff', boxShadow: '0 4px 28px rgba(59,130,246,0.4)' }}
              onMouseEnter={e => { e.currentTarget.style.background = C.gradBtnHov; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 40px rgba(59,130,246,0.55)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = C.gradBtn; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 28px rgba(59,130,246,0.4)'; }}>
              Começar em 5 minutos <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              className="flex items-center gap-2 px-8 py-4 rounded text-sm font-medium transition-all duration-150"
              style={{ border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.03)', color: C.textMuted }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.borderHov; e.currentTarget.style.color = C.textPri; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; }}>
              Ver como funciona
            </button>
          </div>

          {/* ── BIG PRODUCT MOCKUP ─────────────────────── */}
          <div className="relative max-w-5xl mx-auto" style={{ width: '70%', minWidth: '320px' }}>
            {/* Glow */}
            <div aria-hidden className="absolute -inset-[1px] rounded pointer-events-none" style={{ background: `linear-gradient(135deg,rgba(59,130,246,0.3),rgba(6,182,212,0.15),rgba(16,185,129,0.08))`, filter: 'blur(1.5px)', borderRadius: '18px' }} />
            <div className="relative rounded overflow-hidden" style={{ boxShadow: '0 40px 80px rgba(0,0,0,0.6)', border: `1px solid ${C.border}`, borderRadius: '16px', background: '#0A1016' }}>

              {/* Titlebar */}
              <div className="flex items-center justify-between px-5 py-3.5" style={{ background: '#070D14', borderBottom: `1px solid ${C.border}` }}>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1.5">
                    {['#FF5F57', '#FEBC2E', '#28C840'].map(c => <div key={c} className="w-3 h-3 rounded-full" style={{ background: c }} />)}
                  </div>
                  <div className="rounded px-16 py-1 text-[10px] font-mono" style={{ background: 'rgba(255,255,255,0.04)', color: '#3D5470' }}>
                    Workflow Manager — Execução de Processo · Pedido de Compra
                  </div>
                </div>
                <div className="flex gap-1.5">
                  {([['ATUAL', 'rgba(59,130,246,0.18)', '#93C5FD', 'rgba(59,130,246,0.28)'], ['MELHORIA', 'transparent', '#3D5470', C.border], ['FUTURO', 'transparent', '#3D5470', C.border]] as [string, string, string, string][]).map(([m, bg, col, bd]) => (
                    <span key={m} className="text-[9px] font-bold px-2.5 py-1 rounded" style={{ background: bg, color: col, border: `1px solid ${bd}`, letterSpacing: '0.05em' }}>{m}</span>
                  ))}
                </div>
              </div>

              {/* App body */}
              <div className="flex" style={{ minHeight: '420px' }}>

                {/* Sidebar */}
                <div className="w-48 shrink-0 flex flex-col py-4 gap-0.5" style={{ background: '#060B12', borderRight: `1px solid ${C.border}` }}>
                  <div className="px-4 pb-2 pt-1 text-[9px] uppercase tracking-widest font-semibold" style={{ color: '#1E3049' }}>Módulos</div>
                  {([
                    { label: 'Processos',   icon: Map,       active: true,  c: '#60A5FA' },
                    { label: 'Workflows',   icon: GitBranch, active: false, c: '#1E3049' },
                    { label: 'Monitor',     icon: Activity,  active: false, c: '#1E3049' },
                    { label: 'Melhorias',   icon: Zap,       active: false, c: '#1E3049' },
                    { label: 'Modelos',     icon: Shield,    active: false, c: '#1E3049' },
                    { label: 'Gamificação', icon: Trophy,    active: false, c: '#1E3049' },
                  ] as { label: string; icon: React.ElementType; active: boolean; c: string }[]).map(item => (
                    <div key={item.label} className="flex items-center gap-2.5 mx-2 px-3 py-2.5 rounded text-[11px] font-medium" style={{ background: item.active ? 'rgba(59,130,246,0.14)' : 'transparent', color: item.c, border: item.active ? '1px solid rgba(59,130,246,0.22)' : '1px solid transparent' }}>
                      <item.icon className="w-3.5 h-3.5 shrink-0" />{item.label}
                    </div>
                  ))}

                  <div className="px-4 pb-2 pt-5 text-[9px] uppercase tracking-widest font-semibold" style={{ color: '#1E3049' }}>Projetos</div>
                  {(['Procurement Q1', 'Linha de Prod.', 'Atend. Cliente'].map(p => (
                    <div key={p} className="mx-2 px-3 py-2 rounded text-[10px]" style={{ color: '#2A4060' }}>{p}</div>
                  )))}
                </div>

                {/* Main canvas area */}
                <div className="flex-1 flex flex-col p-6 gap-5 overflow-hidden">

                  {/* Top bar */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <span className="text-[13px] font-semibold" style={{ color: C.textPri }}>Processo: Pedido de Compra</span>
                      <span className="text-[9px] px-2 py-0.5 rounded-sm font-bold" style={{ background: 'rgba(16,185,129,0.12)', color: '#34D399', border: '1px solid rgba(16,185,129,0.22)' }}>MELHORIA ATIVA</span>
                    </div>
                    <div className="flex items-center gap-5">
                      {([['Lead Time', '8.2d', '#06B6D4'], ['SLA', '96.4%', '#10B981'], ['WIP', '7/10', '#F59E0B']] as [string, string, string][]).map(([k, v, c]) => (
                        <div key={k} className="text-center">
                          <div className="text-[9px] font-medium" style={{ color: C.textMuted }}>{k}</div>
                          <div className="text-[12px] font-bold" style={{ color: c }}>{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* VSM nodes row */}
                  <div className="flex items-stretch gap-1.5">
                    {([
                      { l: 'Solicitação', t: '1.0d', va: '0.4h', c: '#06B6D4', bg: 'rgba(6,182,212,0.08)', bd: 'rgba(6,182,212,0.22)' },
                      { l: 'Cotação',     t: '2.0d', va: '1.2h', c: '#3B82F6', bg: 'rgba(59,130,246,0.08)', bd: 'rgba(59,130,246,0.22)' },
                      { l: 'Aprovação',   t: '3.2d', va: '0.5h', c: '#F59E0B', bg: 'rgba(245,158,11,0.08)', bd: 'rgba(245,158,11,0.28)' },
                      { l: 'Emissão NF',  t: '1.5d', va: '0.8h', c: '#8B5CF6', bg: 'rgba(139,92,246,0.08)', bd: 'rgba(139,92,246,0.22)' },
                      { l: 'Entrega',     t: '0.5d', va: '0.1h', c: '#10B981', bg: 'rgba(16,185,129,0.08)', bd: 'rgba(16,185,129,0.22)' },
                    ] as { l: string; t: string; va: string; c: string; bg: string; bd: string }[]).map((n, i, arr) => (
                      <React.Fragment key={n.l}>
                        <div className="flex-1 rounded p-3 min-w-0" style={{ background: n.bg, border: `1px solid ${n.bd}` }}>
                          <div className="text-[9px] font-bold mb-1.5 truncate uppercase tracking-wide" style={{ color: n.c }}>{n.l}</div>
                          <div className="text-[15px] font-bold" style={{ color: C.textPri }}>{n.t}</div>
                          <div className="text-[9px] mt-0.5" style={{ color: C.textMuted }}>VA: {n.va}</div>
                        </div>
                        {i < arr.length - 1 && <div className="flex items-center"><ChevronRight className="w-3 h-3 shrink-0" style={{ color: '#1E3049' }} /></div>}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Workflow strip */}
                  <div className="rounded p-4" style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.border}` }}>
                    <div className="text-[9px] font-bold uppercase tracking-widest mb-3" style={{ color: '#1E3049' }}>Execução — Etapas em andamento</div>
                    <div className="flex gap-2">
                      {([
                        { l: 'Analisar cotações',  s: 'CONCLUÍDO',    c: '#10B981', bg: 'rgba(16,185,129,0.08)' },
                        { l: 'Aprovar pedido',     s: 'EM PROGRESSO', c: '#3B82F6', bg: 'rgba(59,130,246,0.1)' },
                        { l: 'Emitir NF',          s: 'AGUARDANDO',   c: '#3D5470', bg: 'rgba(255,255,255,0.02)' },
                        { l: 'Confirmar entrega',  s: 'AGUARDANDO',   c: '#3D5470', bg: 'rgba(255,255,255,0.02)' },
                      ] as { l: string; s: string; c: string; bg: string }[]).map(t => (
                        <div key={t.l} className="flex-1 rounded p-2.5 min-w-0" style={{ background: t.bg, border: `1px solid ${C.border}` }}>
                          <div className="text-[9px] truncate mb-1" style={{ color: C.textMuted }}>{t.l}</div>
                          <div className="text-[9px] font-bold tracking-wide" style={{ color: t.c }}>{t.s}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* KPI strip */}
                  <div className="grid grid-cols-4 gap-3">
                    {([
                      { l: 'Lead Time Total', v: '8.2d',   d: '↓ 22% este mês', c: '#10B981' },
                      { l: 'SLA Compliance',  v: '96.4%',  d: '↑ 4.2% vs meta', c: '#3B82F6' },
                      { l: 'Melhorias ativas',  v: '12',     d: '3 concluídas hoje', c: '#06B6D4' },
                      { l: 'WIP Atual',       v: '7 etapas', d: 'limite: 10', c: '#8B5CF6' },
                    ] as { l: string; v: string; d: string; c: string }[]).map(k => (
                      <div key={k.l} className="rounded p-3" style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.border}` }}>
                        <div className="text-[9px] mb-1.5 font-medium" style={{ color: C.textMuted }}>{k.l}</div>
                        <div className="text-[13px] font-bold" style={{ color: k.c }}>{k.v}</div>
                        <div className="text-[9px] mt-0.5" style={{ color: '#1E3049' }}>{k.d}</div>
                      </div>
                    ))}
                  </div>

                </div>
              </div>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="flex flex-col items-center gap-2 mt-12" style={{ opacity: 0.2 }}>
            <div className="text-[10px] uppercase tracking-widest" style={{ color: C.textMuted }}>Scroll</div>
            <div className="w-5 h-8 rounded-full border flex items-start justify-center pt-1.5" style={{ borderColor: '#1E3049' }}>
              <div className="w-1 h-2 rounded-full animate-bounce" style={{ background: C.accentA }} />
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
           TRUST BAR
      ══════════════════════════════════════════════════════════════ */}
      <section style={{ background: C.bgAlt, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-5xl mx-auto px-8 py-8">
          <p className="text-center text-[10px] uppercase tracking-widest mb-5" style={{ color: '#1A2A40' }}>Empresas que operam com Workflow Manager</p>
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-14">
            {CLIENTS.map(name => (
              <div key={name} className="text-sm font-bold select-none cursor-default transition-colors duration-150" style={{ color: '#1A2A40' }}
                onMouseEnter={e => (e.currentTarget.style.color = C.textMuted)}
                onMouseLeave={e => (e.currentTarget.style.color = '#1A2A40')}>{name}</div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
           PROBLEM BLOCK
      ══════════════════════════════════════════════════════════════ */}
      <section style={{ background: C.bgMain, paddingTop: '100px', paddingBottom: '100px' }}>
        <div className="max-w-3xl mx-auto px-8 text-center">
          <h2 className="font-bold mb-6" style={{ fontSize: '2.25rem', letterSpacing: '-0.02em', color: C.textPri }}>Processos existem. Execução não.</h2>
          <p className="mb-5" style={{ color: C.textSec, fontSize: '1.125rem', lineHeight: '1.8', maxWidth: '600px', margin: '0 auto' }}>
            A maioria das empresas tem processos definidos — mas não consegue garantir que eles aconteçam como deveriam.
          </p>
          <p className="mb-5" style={{ color: C.textSec, fontSize: '1.125rem', lineHeight: '1.8', maxWidth: '600px', margin: '0 auto' }}>
            Tarefas se perdem, prazos estouram e gargalos só aparecem quando já é tarde.
          </p>
          <p style={{ color: C.textPri, fontSize: '1.25rem', lineHeight: '1.6', fontWeight: 600, maxWidth: '600px', margin: '0 auto' }}>
            O problema não é gestão. É execução.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
           HOW IT WORKS
      ══════════════════════════════════════════════════════════════ */}
      <section id="how-it-works" style={{ background: C.bgMain, minHeight: '90vh', paddingTop: '120px', paddingBottom: '120px' }}>
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <div className="inline-block text-[10px] font-bold uppercase tracking-widest mb-5 px-3 py-1.5 rounded-sm" style={{ background: 'rgba(59,130,246,0.1)', color: '#93C5FD', border: '1px solid rgba(59,130,246,0.22)' }}>Como funciona</div>
            <h2 className="font-bold mb-4" style={{ fontSize: '2.25rem', letterSpacing: '-0.02em', color: C.textPri }}>Do planejamento à execução em 4 passos</h2>
            <p style={{ color: C.textSec, fontSize: '1.125rem', maxWidth: '640px', margin: '0 auto', lineHeight: '1.7' }}>Um fluxo simples e contínuo: do desenho do processo ao resultado mensurável.</p>
          </div>

          {/* Flow pipeline */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-16">
            {([
              { l: 'PLANEJAR',  bg: '#0E4A5C', t: '#67E8F9' },
              { l: 'EXECUTAR',  bg: '#1E3A8A', t: '#93C5FD' },
              { l: 'MONITORAR', bg: '#4C1D95', t: '#C4B5FD' },
              { l: 'MELHORAR',  bg: '#064E3B', t: '#6EE7B7' },
            ] as { l: string; bg: string; t: string }[]).map((item, i, arr) => (
              <React.Fragment key={item.l}>
                <div className="text-[11px] font-bold px-6 py-3 rounded tracking-widest" style={{ background: item.bg, color: item.t, letterSpacing: '0.08em' }}>{item.l}</div>
                {i < arr.length - 1 && <ArrowRight className="w-4 h-4" style={{ color: '#1E3049' }} />}
              </React.Fragment>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {HOW_IT_WORKS.map(item => (
              <div key={item.step} className="rounded p-7 transition-all duration-200 cursor-default" style={{ ...cardBase }}
                onMouseEnter={e => onCardEnter(e, `${item.color}35`)}
                onMouseLeave={onCardLeave}>
                <div className="font-mono text-xs font-bold mb-5" style={{ color: item.color }}>{item.step}</div>
                <div className="w-11 h-11 rounded flex items-center justify-center mb-5" style={{ background: `${item.color}12`, border: `1px solid ${item.color}28` }}>
                  <item.icon className="w-5 h-5" style={{ color: item.color }} />
                </div>
                <h3 className="font-bold mb-2.5" style={{ color: C.textPri, fontSize: '1rem' }}>{item.label}</h3>
                <p className="text-sm leading-relaxed" style={{ color: C.textMuted }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
           EXPLORE A PLATAFORMA
      ══════════════════════════════════════════════════════════════ */}
      <section id="explore" style={{ background: C.bgAlt, minHeight: '90vh', paddingTop: '120px', paddingBottom: '120px' }}>
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-20">
            <div className="inline-block text-[10px] font-bold uppercase tracking-widest mb-5 px-3 py-1.5 rounded-sm" style={{ background: 'rgba(6,182,212,0.09)', color: C.accentB, border: `1px solid rgba(6,182,212,0.2)` }}>Plataforma</div>
            <h2 className="font-bold mb-4" style={{ fontSize: '2.25rem', letterSpacing: '-0.02em', color: C.textPri }}>Tudo que sua operação precisa</h2>
            <p style={{ color: C.textSec, fontSize: '1.125rem', maxWidth: '640px', margin: '0 auto', lineHeight: '1.7' }}>Módulos integrados para planejar, executar e controlar processos de ponta a ponta.</p>
          </div>

          <div className="flex flex-col gap-20">

            {/* Block 1 — VSM Canvas (text left, image right) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-block text-[10px] font-bold uppercase tracking-widest mb-4 px-2.5 py-1 rounded" style={{ background: 'rgba(6,182,212,0.09)', color: C.accentB }}>01 — Processo Visual</div>
                <h3 className="font-bold mb-4" style={{ fontSize: '1.5rem', letterSpacing: '-0.02em', color: C.textPri }}>Visualize o fluxo completo e identifique onde está travando</h3>
                <p className="text-sm leading-relaxed mb-6" style={{ color: C.textSec, maxWidth: '480px' }}>Desenhe seus processos em um canvas visual profissional. Veja cada etapa, responsável e prazo de forma clara — sem documentos estáticos nem planilhas.</p>
                <ul className="space-y-3">
                  {['Canvas infinito com zoom e navegação profissional', 'Indicadores de tempo por etapa', 'Comparação entre processo atual e futuro', 'Exportação para PDF e compartilhamento'].map(f => (
                    <li key={f} className="flex items-center gap-3 text-sm" style={{ color: C.textSec }}>
                      <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: C.accentB }} />{f}
                    </li>
                  ))}
                </ul>
              </div>
              {/* VSM mockup */}
              <div className="rounded overflow-hidden" style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.5)', border: `1px solid rgba(6,182,212,0.18)` }}>
                <div className="px-4 py-3 flex items-center justify-between" style={{ background: '#060B12', borderBottom: `1px solid ${C.border}` }}>
                  <span className="text-[10px] font-mono" style={{ color: '#2A4060' }}>Processo Visual — Compras</span>
                  <span className="text-[9px] px-2 py-0.5 rounded font-bold" style={{ background: 'rgba(6,182,212,0.1)', color: C.accentB }}>ATUAL</span>
                </div>
                <div className="p-5" style={{ background: C.bgCard }}>
                  <div className="text-[9px] mb-4 uppercase tracking-widest font-semibold" style={{ color: '#1E3049' }}>Fluxo de valor — Procurement</div>
                  <div className="flex items-center gap-1.5 mb-5">
                    {([
                      { l: 'Requisição',  t: '1d',   c: '#06B6D4' },
                      { l: 'Aprovação',   t: '2d',   c: '#F59E0B' },
                      { l: 'Compra',      t: '1.5d', c: '#3B82F6' },
                      { l: 'Recebimento', t: '0.5d', c: '#10B981' },
                    ] as { l: string; t: string; c: string }[]).map((n, i, arr) => (
                      <React.Fragment key={n.l}>
                        <div className="flex-1 rounded p-3 text-center min-w-0" style={{ background: `${n.c}10`, border: `1px solid ${n.c}28` }}>
                          <div className="text-[9px] font-bold truncate mb-1" style={{ color: n.c }}>{n.l}</div>
                          <div className="text-sm font-bold" style={{ color: C.textPri }}>{n.t}</div>
                        </div>
                        {i < arr.length - 1 && <ChevronRight className="w-3 h-3 shrink-0" style={{ color: '#1E3049' }} />}
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {([['Lead Time', '5.0d', '#06B6D4'], ['Valor Agregado', '2.8h', '#10B981'], ['Eficiência', '23%', '#F59E0B']] as [string, string, string][]).map(([l, v, c]) => (
                      <div key={l} className="rounded p-3" style={{ background: C.bgMain, border: `1px solid ${C.border}` }}>
                        <div className="text-[9px] mb-1" style={{ color: C.textMuted }}>{l}</div>
                        <div className="text-base font-bold" style={{ color: c }}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Block 2 — Workflow Engine (image left, text right) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Workflow mockup */}
              <div className="rounded overflow-hidden order-2 lg:order-1" style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.5)', border: `1px solid rgba(59,130,246,0.18)` }}>
                <div className="px-4 py-3 flex items-center justify-between" style={{ background: '#060B12', borderBottom: `1px solid ${C.border}` }}>
                  <span className="text-[10px] font-mono" style={{ color: '#2A4060' }}>Execução de Processo — Aprovação de Pedido</span>
                  <span className="text-[9px] px-2 py-0.5 rounded font-bold" style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981' }}>3 ATIVOS</span>
                </div>
                <div className="p-5 flex flex-col gap-2.5" style={{ background: C.bgCard }}>
                  {([
                    { l: 'Receber solicitação',  resp: 'João Silva',      sla: '1h',  status: 'DONE',        c: '#10B981' },
                    { l: 'Verificar orçamento',  resp: 'Maria Costa',     sla: '4h',  status: 'IN PROGRESS', c: '#3B82F6' },
                    { l: 'Aprovação gerencial',  resp: 'Dir. Financeiro', sla: '24h', status: 'PENDING',     c: '#F59E0B' },
                    { l: 'Liberar pedido',        resp: 'Logística',       sla: '2h',  status: 'PENDING',     c: '#3D5470' },
                  ] as { l: string; resp: string; sla: string; status: string; c: string }[]).map(t => (
                    <div key={t.l} className="flex items-center gap-3 rounded px-4 py-3.5" style={{ background: C.bgMain, border: `1px solid ${t.status === 'IN PROGRESS' ? 'rgba(59,130,246,0.25)' : C.border}` }}>
                      <div className="w-2 h-2 rounded-full shrink-0" style={{ background: t.c }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-semibold truncate" style={{ color: C.textPri }}>{t.l}</div>
                        <div className="text-[10px] mt-0.5" style={{ color: C.textMuted }}>{t.resp} · SLA: {t.sla}</div>
                      </div>
                      <div className="text-[9px] font-bold tracking-wide" style={{ color: t.c }}>{t.status}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-block text-[10px] font-bold uppercase tracking-widest mb-4 px-2.5 py-1 rounded" style={{ background: 'rgba(59,130,246,0.09)', color: C.accentA }}>02 — Execução de Processos</div>
                <h3 className="font-bold mb-4" style={{ fontSize: '1.5rem', letterSpacing: '-0.02em', color: C.textPri }}>Processos viram execução real</h3>
                <p className="text-sm leading-relaxed mb-6" style={{ color: C.textSec, maxWidth: '480px' }}>Transforme seus processos em etapas executáveis com responsáveis e prazos claros. Modelos reutilizáveis garantem padronização em toda a operação.</p>
                <ul className="space-y-3">
                  {['Prazos por etapa com alertas automáticos', 'Responsáveis e equipes por etapa', 'Modelos de processo reutilizáveis', 'Rastreabilidade e histórico completo'].map(f => (
                    <li key={f} className="flex items-center gap-3 text-sm" style={{ color: C.textSec }}>
                      <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: C.accentA }} />{f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Block 3 — Monitor Operacional (text left, image right) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-block text-[10px] font-bold uppercase tracking-widest mb-4 px-2.5 py-1 rounded" style={{ background: 'rgba(139,92,246,0.09)', color: '#A78BFA' }}>03 — Monitoramento</div>
                <h3 className="font-bold mb-4" style={{ fontSize: '1.5rem', letterSpacing: '-0.02em', color: C.textPri }}>Veja o que está acontecendo agora na sua operação</h3>
                <p className="text-sm leading-relaxed mb-6" style={{ color: C.textSec, maxWidth: '480px' }}>Acompanhe gargalos, filas e desempenho em tempo real. Detecte desvios automaticamente e tome ação antes que os problemas se acumulem.</p>
                <ul className="space-y-3">
                  {['Tempo por etapa em tempo real', 'Alertas de gargalo automáticos', 'Dashboard de prazos e cumprimento', 'Ações de melhoria integradas'].map(f => (
                    <li key={f} className="flex items-center gap-3 text-sm" style={{ color: C.textSec }}>
                      <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: '#A78BFA' }} />{f}
                    </li>
                  ))}
                </ul>
              </div>
              {/* Monitor mockup */}
              <div className="rounded overflow-hidden" style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.5)', border: '1px solid rgba(139,92,246,0.18)' }}>
                <div className="px-4 py-3 flex items-center justify-between" style={{ background: '#060B12', borderBottom: `1px solid ${C.border}` }}>
                  <span className="text-[10px] font-mono" style={{ color: '#2A4060' }}>Monitor Operacional — Real-time</span>
                  <span className="text-[9px] px-2 py-0.5 rounded font-bold animate-pulse" style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981' }}>● LIVE</span>
                </div>
                <div className="p-5" style={{ background: C.bgCard }}>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {([
                      { l: 'Lead Time Médio', v: '8.2d',     d: '↓ 22%',           c: '#10B981' },
                      { l: 'SLA Compliance',  v: '96.4%',    d: '↑ 4.2%',          c: '#3B82F6' },
                      { l: 'Gargalo Atual',    v: 'Aprovação', d: '3.2d fila',      c: '#F59E0B' },
                      { l: 'Melhorias abertas', v: '12',        d: '3 concluídas',   c: '#8B5CF6' },
                    ] as { l: string; v: string; d: string; c: string }[]).map(k => (
                      <div key={k.l} className="rounded p-3.5" style={{ background: C.bgMain, border: `1px solid ${C.border}` }}>
                        <div className="text-[9px] mb-1.5 font-medium" style={{ color: C.textMuted }}>{k.l}</div>
                        <div className="text-lg font-bold" style={{ color: k.c }}>{k.v}</div>
                        <div className="text-[9px] mt-0.5 font-semibold" style={{ color: k.c, opacity: 0.65 }}>{k.d}</div>
                      </div>
                    ))}
                  </div>
                  {/* Bar chart */}
                  <div className="rounded p-4" style={{ background: C.bgMain, border: `1px solid ${C.border}` }}>
                    <div className="text-[9px] mb-3 uppercase tracking-widest font-bold" style={{ color: '#1E3049' }}>Lead time por etapa (dias)</div>
                    <div className="flex items-end gap-2 h-16">
                      {([
                        { l: 'Sol.',  v: 30,  c: '#06B6D4' },
                        { l: 'Cot.',  v: 65,  c: '#3B82F6' },
                        { l: 'Apr.',  v: 100, c: '#F59E0B' },
                        { l: 'NF',   v: 45,  c: '#8B5CF6' },
                        { l: 'Ent.', v: 15,  c: '#10B981' },
                      ] as { l: string; v: number; c: string }[]).map(b => (
                        <div key={b.l} className="flex-1 flex flex-col items-center gap-1.5">
                          <div className="w-full rounded-t" style={{ height: `${b.v}%`, background: `${b.c}35`, border: `1px solid ${b.c}30` }} />
                          <div className="text-[8px] font-medium" style={{ color: '#1E3049' }}>{b.l}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
           FEATURES / CAPACIDADES
      ══════════════════════════════════════════════════════════════ */}
      <section id="features" style={{ background: C.bgMain, minHeight: '90vh', paddingTop: '120px', paddingBottom: '120px' }}>
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="font-bold mb-4" style={{ fontSize: '2.25rem', letterSpacing: '-0.02em', color: C.textPri }}>Capacidades da Plataforma</h2>
            <p style={{ color: C.textSec, fontSize: '1.125rem', maxWidth: '640px', margin: '0 auto', lineHeight: '1.7' }}>Tudo que sua operação precisa para executar com controle, integrado em um único produto.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(feat => (
              <div key={feat.title} className="rounded p-7 transition-all duration-200 cursor-default" style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: '16px' }}
                onMouseEnter={e => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.transform = 'translateY(-4px)';
                  el.style.borderColor = `${feat.accent}35`;
                  el.style.boxShadow = `0 12px 40px rgba(0,0,0,0.3), 0 0 30px ${feat.accent}0A`;
                  el.style.background = feat.bg;
                }}
                onMouseLeave={e => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.transform = 'none';
                  el.style.borderColor = C.border;
                  el.style.boxShadow = 'none';
                  el.style.background = C.bgCard;
                }}>
                <div className="w-12 h-12 rounded flex items-center justify-center mb-6" style={{ background: feat.bg, border: `1px solid ${feat.accent}25` }}>
                  <feat.icon className="w-6 h-6" style={{ color: feat.accent }} />
                </div>
                <h3 className="font-bold mb-2.5" style={{ color: C.textPri, fontSize: '1rem' }}>{feat.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: C.textMuted }}>{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
           WHY DIFFERENT
      ══════════════════════════════════════════════════════════════ */}
      <section style={{ background: C.bgAlt, paddingTop: '120px', paddingBottom: '120px' }}>
        <div className="max-w-4xl mx-auto px-8">
          <div className="text-center mb-14">
            <h2 className="font-bold mb-4" style={{ fontSize: '2.25rem', letterSpacing: '-0.02em', color: C.textPri }}>Não é gestão de tarefas. É execução operacional.</h2>
            <p style={{ color: C.textSec, fontSize: '1.125rem', maxWidth: '640px', margin: '0 auto', lineHeight: '1.7' }}>Diferente de ferramentas comuns de gestão, o Workflow Manager conecta o desenho do processo com a execução real da equipe.</p>
          </div>
          <div className="rounded overflow-hidden" style={{ border: `1px solid ${C.border}` }}>
            <div className="grid grid-cols-2 text-[10px] font-bold uppercase tracking-widest" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <div className="px-7 py-5" style={{ color: C.textMuted, borderRight: `1px solid ${C.border}` }}>Ferramentas comuns</div>
              <div className="px-7 py-5" style={{ color: C.accentA }}>Workflow Manager</div>
            </div>
            {COMPARISON.map((row, i) => (
              <div key={i} className="grid grid-cols-2" style={{ background: i % 2 === 0 ? 'rgba(255,255,255,0.015)' : 'transparent' }}>
                <div className="px-7 py-5 text-sm flex items-center gap-3" style={{ color: C.textMuted, borderRight: `1px solid ${C.border}` }}>
                  <X className="w-4 h-4 shrink-0" style={{ color: '#EF4444', opacity: 0.45 }} />{row.traditional}
                </div>
                <div className="px-7 py-5 text-sm font-medium flex items-center gap-3" style={{ color: C.textSec }}>
                  <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: '#10B981' }} />{row.wfm}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
           SCALE STATS
      ══════════════════════════════════════════════════════════════ */}
      <section style={{ background: C.bgMain, paddingTop: '120px', paddingBottom: '120px' }}>
        <div className="max-w-5xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="font-bold mb-4" style={{ fontSize: '2.25rem', letterSpacing: '-0.02em', color: C.textPri }}>Resultados reais de operações reais</h2>
            <p style={{ color: C.textSec, fontSize: '1.125rem', maxWidth: '640px', margin: '0 auto', lineHeight: '1.7' }}>Números de equipes que já controlam a execução com Workflow Manager.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {SCALE_STATS.map(r => (
              <div key={r.value} className="rounded p-10 text-center transition-all duration-200 cursor-default" style={{ ...cardBase }}
                onMouseEnter={e => onCardEnter(e, `${r.accent}40`)}
                onMouseLeave={onCardLeave}>
                <div className="w-11 h-11 rounded flex items-center justify-center mx-auto mb-6" style={{ background: `${r.accent}12`, border: `1px solid ${r.accent}22` }}>
                  <r.icon className="w-5 h-5" style={{ color: r.accent }} />
                </div>
                <div className="font-bold tracking-tight mb-2" style={{ fontSize: '64px', lineHeight: '1', color: r.accent }}>{r.value}</div>
                <div className="text-sm font-medium" style={{ color: C.textMuted }}>{r.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
           TESTIMONIALS
      ══════════════════════════════════════════════════════════════ */}
      <section style={{ background: C.bgAlt, paddingTop: '120px', paddingBottom: '120px' }}>
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="font-bold mb-4" style={{ fontSize: '2.25rem', letterSpacing: '-0.02em', color: C.textPri }}>Quem usa, recomenda</h2>
            <p style={{ color: C.textSec, fontSize: '1.125rem', maxWidth: '640px', margin: '0 auto', lineHeight: '1.7' }}>Operações reais que ganharam controle com Workflow Manager.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map(t => (
              <div key={t.name} className="rounded p-8 flex flex-col transition-all duration-200 cursor-default" style={{ ...cardBase }}
                onMouseEnter={e => onCardEnter(e, `${t.color}28`)}
                onMouseLeave={onCardLeave}>
                <div className="flex gap-0.5 mb-6">{[...Array(5)].map((_, i) => <span key={i} style={{ color: '#F59E0B', fontSize: '14px' }}>★</span>)}</div>
                <p className="text-sm leading-relaxed flex-1 mb-7" style={{ color: C.textSec }}>"{t.text}"</p>
                <div className="flex items-center gap-3 pt-6" style={{ borderTop: `1px solid ${C.border}` }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: `${t.color}18`, color: t.color, border: `1px solid ${t.color}30` }}>{t.initials}</div>
                  <div>
                    <div className="text-sm font-semibold leading-tight" style={{ color: C.textPri }}>{t.name}</div>
                    <div className="text-xs mt-0.5" style={{ color: C.textMuted }}>{t.role} · {t.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
           PRICING
      ══════════════════════════════════════════════════════════════ */}
      <section id="pricing" style={{ background: C.bgMain, minHeight: '90vh', paddingTop: '120px', paddingBottom: '120px' }}>
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-8">
            <div className="inline-block text-[10px] font-bold uppercase tracking-widest mb-5 px-3 py-1.5 rounded-sm" style={{ background: 'rgba(16,185,129,0.09)', color: '#34D399', border: '1px solid rgba(16,185,129,0.2)' }}>Planos</div>
            <h2 className="font-bold mb-4" style={{ fontSize: '2.25rem', letterSpacing: '-0.02em', color: C.textPri }}>Planos e Preços</h2>
            <p style={{ color: C.textSec, fontSize: '1.125rem', maxWidth: '640px', margin: '0 auto', lineHeight: '1.7' }}>
              Comece gratuitamente. Escale conforme sua operação cresce.
            </p>
          </div>

          {/* Billing toggle */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex rounded p-1" style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}` }}>
              {(['monthly', 'annual'] as const).map(cycle => (
                <button key={cycle} onClick={() => setBillingCycle(cycle)}
                  className="px-6 py-2.5 rounded text-xs font-semibold transition-all duration-150"
                  style={{
                    background: billingCycle === cycle ? 'rgba(59,130,246,0.22)' : 'transparent',
                    color: billingCycle === cycle ? '#93C5FD' : C.textMuted,
                    border: billingCycle === cycle ? '1px solid rgba(59,130,246,0.35)' : '1px solid transparent',
                  }}>
                  {cycle === 'monthly' ? 'Mensal' : 'Anual'}
                  {cycle === 'annual' && <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: 'rgba(16,185,129,0.14)', color: '#34D399' }}>-17%</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
            {PLANS.map(plan => {
              const p = getPrice(plan);
              return (
                <div key={plan.slug} className="rounded p-7 flex flex-col relative transition-all duration-200" style={{
                  background: plan.highlighted ? 'linear-gradient(135deg,rgba(59,130,246,0.14) 0%,rgba(6,182,212,0.05) 100%)' : C.bgCard,
                  border: plan.highlighted ? '1px solid rgba(59,130,246,0.45)' : `1px solid ${C.border}`,
                  boxShadow: plan.highlighted ? '0 0 60px rgba(59,130,246,0.1), inset 0 0 40px rgba(59,130,246,0.04)' : 'none',
                  borderRadius: '16px',
                }}>
                  {plan.highlighted && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-[11px] font-bold px-5 py-1.5 rounded-sm whitespace-nowrap" style={{ background: 'linear-gradient(90deg,#2563EB,#06B6D4)', color: '#fff', boxShadow: '0 0 20px rgba(37,99,235,0.5)' }}>
                      Mais popular
                    </div>
                  )}

                  <h3 className="font-bold text-lg mb-1" style={{ color: C.textPri }}>{plan.name}</h3>
                  <p className="text-xs mb-6" style={{ color: C.textMuted }}>{plan.description}</p>

                  <div className="mb-7">
                    {p !== null ? (
                      <>
                        <div className="flex items-baseline gap-1.5">
                          <span className="font-bold" style={{ fontSize: '2.5rem', letterSpacing: '-0.02em', color: C.textPri, lineHeight: '1' }}>{p === 0 ? 'Grátis' : `R$${p.toLocaleString('pt-BR')}`}</span>
                          {p > 0 && <span className="text-sm" style={{ color: C.textMuted }}>/mês</span>}
                        </div>
                        {p > 0 && billingCycle === 'annual' && <div className="text-[11px] mt-1" style={{ color: '#1E3049' }}>cobrado anualmente</div>}
                      </>
                    ) : (
                      <span className="text-xl font-semibold" style={{ color: C.textMuted }}>Sob consulta</span>
                    )}
                  </div>

                  <ul className="flex-1 space-y-3.5 mb-8">
                    {plan.features.map(feat => (
                      <li key={feat} className="flex items-start gap-2.5 text-sm" style={{ color: C.textSec }}>
                        <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: plan.highlighted ? '#10B981' : C.accentA }} />{feat}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={plan.slug === 'enterprise' ? sales : go}
                    className="w-full py-3.5 rounded text-sm font-bold transition-all duration-150"
                    style={plan.highlighted
                      ? { background: C.gradBtn, color: '#fff', boxShadow: '0 4px 20px rgba(59,130,246,0.4)' }
                      : { background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: C.textMuted }}
                    onMouseEnter={e => {
                      if (plan.highlighted) { e.currentTarget.style.background = C.gradBtnHov; e.currentTarget.style.boxShadow = '0 6px 30px rgba(59,130,246,0.55)'; }
                      else { (e.currentTarget as HTMLButtonElement).style.color = C.textPri; (e.currentTarget as HTMLButtonElement).style.borderColor = C.borderHov; }
                    }}
                    onMouseLeave={e => {
                      if (plan.highlighted) { e.currentTarget.style.background = C.gradBtn; e.currentTarget.style.boxShadow = '0 4px 20px rgba(59,130,246,0.4)'; }
                      else { (e.currentTarget as HTMLButtonElement).style.color = C.textMuted; (e.currentTarget as HTMLButtonElement).style.borderColor = C.border; }
                    }}>
                    {plan.cta}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Micro-trust signals */}
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-3 mt-10">
            {['Sem cartão de crédito', 'Cancelamento a qualquer momento', 'Setup em menos de 5 minutos'].map(t => (
              <div key={t} className="flex items-center gap-2 text-sm" style={{ color: C.textMuted }}>
                <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: C.accentA, opacity: 0.7 }} />{t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
           BOTTOM CTA
      ══════════════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden" style={{ background: C.bgAlt, paddingTop: '120px', paddingBottom: '120px' }}>
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ backgroundImage: `linear-gradient(${C.border} 1px,transparent 1px),linear-gradient(90deg,${C.border} 1px,transparent 1px)`, backgroundSize: '64px 64px' }} />
        <div aria-hidden className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-96 pointer-events-none" style={{ background: 'radial-gradient(ellipse,rgba(59,130,246,0.12) 0%,transparent 68%)', filter: 'blur(48px)' }} />
        <div aria-hidden className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-64 pointer-events-none" style={{ background: 'radial-gradient(ellipse,rgba(6,182,212,0.08) 0%,transparent 68%)', filter: 'blur(40px)' }} />

        <div className="relative max-w-3xl mx-auto px-8 text-center">
          <div className="inline-flex items-center gap-2.5 text-[10px] font-bold uppercase tracking-widest px-4 py-2 rounded-sm mb-8" style={{ background: 'rgba(59,130,246,0.1)', color: '#93C5FD', border: '1px solid rgba(59,130,246,0.22)' }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: C.accentA }} />Comece hoje
          </div>
          <h2 className="font-bold mb-5" style={{ fontSize: 'clamp(2rem,4vw,3rem)', letterSpacing: '-0.02em', color: C.textPri }}>
            Pronto para ter controle real<br />da sua operação?
          </h2>
          <p className="mb-4" style={{ color: C.textSec, fontSize: '1.125rem', maxWidth: '520px', margin: '0 auto 2rem', lineHeight: '1.7' }}>
            Comece agora. Sem cartão. Configurado em poucos minutos.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-10">
            <button onClick={go}
              className="inline-flex items-center gap-2 px-10 py-4 rounded text-sm font-bold transition-all duration-150"
              style={{ background: C.gradBtn, color: '#fff', boxShadow: '0 8px 40px rgba(59,130,246,0.4)' }}
              onMouseEnter={e => { e.currentTarget.style.background = C.gradBtnHov; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 14px 50px rgba(59,130,246,0.6)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = C.gradBtn; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 40px rgba(59,130,246,0.4)'; }}>
              Começar gratuitamente <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={sales}
              className="inline-flex items-center gap-2 px-8 py-4 rounded text-sm font-medium transition-all duration-150"
              style={{ border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.03)', color: C.textMuted }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.borderHov; e.currentTarget.style.color = C.textPri; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; }}>
              Falar com especialista
            </button>
          </div>

          {/* Mini dashboard preview under CTA */}
          <div className="mt-16 rounded overflow-hidden mx-auto max-w-2xl opacity-70" style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.5)', border: `1px solid ${C.border}` }}>
            <div className="px-4 py-2.5 flex items-center gap-2" style={{ background: '#060B12', borderBottom: `1px solid ${C.border}` }}>
              <div className="flex gap-1.5">{['#FF5F57', '#FEBC2E', '#28C840'].map(c => <div key={c} className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />)}</div>
              <div className="text-[10px] font-mono ml-2" style={{ color: '#1E3049' }}>Dashboard Operacional</div>
            </div>
            <div className="grid grid-cols-4 gap-2 p-4" style={{ background: C.bgCard }}>
              {([{ l: 'Processos', v: '124', c: '#06B6D4' }, { l: 'SLA', v: '96%', c: '#10B981' }, { l: 'Melhorias', v: '28', c: '#8B5CF6' }, { l: 'Lead Time', v: '7.2d', c: '#3B82F6' }] as { l: string; v: string; c: string }[]).map(k => (
                <div key={k.l} className="rounded p-3 text-center" style={{ background: C.bgMain, border: `1px solid ${C.border}` }}>
                  <div className="text-[9px] mb-1" style={{ color: C.textMuted }}>{k.l}</div>
                  <div className="text-base font-bold" style={{ color: k.c }}>{k.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
           FOOTER
      ══════════════════════════════════════════════════════════════ */}
      <footer className="py-16" style={{ background: '#080E1A', borderTop: `1px solid ${C.border}` }}>
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-14">

            <div>
              <div className="flex items-center gap-3 mb-5">
                <img src="/assets/logo/workflow-icon.png" alt="Workflow Manager" className="w-8 h-8 object-contain" loading="lazy" />
                <div>
                  <div className="text-sm font-bold leading-tight" style={{ color: C.textPri }}>Workflow Manager</div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest leading-tight" style={{ color: C.accentB }}>Sistema de Execução Operacional</div>
                </div>
              </div>
              <p className="text-xs leading-relaxed" style={{ color: '#1A2A40' }}>
                Planejamento de processos.<br />Execução operacional.<br />Monitoramento em tempo real.<br />Melhoria contínua.
              </p>
            </div>

            {([
              { title: 'Produto',  links: [['Como funciona', '#how-it-works'], ['Plataforma', '#explore'], ['Recursos', '#features'], ['Preços', '#pricing']] },
              { title: 'Recursos', links: [['Documentação', '#'], ['Contato', 'mailto:contato@workflowbrasil.com']] },
              { title: 'Legal',    links: [['Termos de Uso', '/termos'], ['Privacidade', '/privacidade']] },
            ] as { title: string; links: [string, string][] }[]).map(col => (
              <div key={col.title}>
                <div className="text-[10px] font-bold uppercase tracking-widest mb-5" style={{ color: '#1A2A40' }}>{col.title}</div>
                <ul className="space-y-3.5">
                  {col.links.map(([label, href]) => (
                    <li key={label}>
                      <a href={href} className="text-xs transition-colors duration-150" style={{ color: '#1A2A40' }}
                        onMouseEnter={e => (e.currentTarget.style.color = C.textMuted)}
                        onMouseLeave={e => (e.currentTarget.style.color = '#1A2A40')}>{label}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 pt-6 text-xs" style={{ borderTop: `1px solid ${C.border}`, color: '#0D1A2E' }}>
            <span>© 2026 Workflow Manager. Todos os direitos reservados.</span>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#22C55E' }} />
              <span style={{ color: '#0A1B0A' }}>Todos os sistemas operacionais</span>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
